import { useCallback } from "react";

import toast from "react-hot-toast";

import { HashIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import TagModalChooser from "components/lib/Choosers/Tags/TagModalChooser";
import type { TagLike } from "components/lib/Tag";

import type { IdeaWithTags } from "../types";

type TagActionProps = { idea: IdeaWithTags };

const TagAction = ({ idea }: TagActionProps) => {
  const user = useCurrentUser();

  const updateTagListWithTag = useCallback(
    (taggedIdea: IdeaWithTags, tag: OpenAPI.Schemas["Tag"]) => {
      const currentChallenge = taggedIdea?.ownerChallenge;
      if (!currentChallenge?.tagList) return currentChallenge;
      const tagList = [...currentChallenge.tagList];
      const index = tagList.findIndex((t) => t._id === tag._id);
      tagList[index] = tag;
      return { ...currentChallenge, tagList };
    },
    [],
  );

  const addTag = useCallback(
    (tag: OpenAPI.Schemas["Tag"]) => {
      api.tags.tagSingle(
        "ideas",
        tag._id,
        idea._id,
        (updatedTag) => {
          const ownerTags = [...(idea?.ownerTags ?? [])];
          ownerTags.push(updatedTag);
          const updatedChallenge = updateTagListWithTag(idea, updatedTag);
          util.onUpdateIdea(idea, {
            ownerTags,
            tags: ownerTags.map((t) => t._id),
            ownerChallenge: updatedChallenge,
          });
        },
        (err) => toast.error(err.message),
      );
    },
    [idea, updateTagListWithTag],
  );

  const removeTag = useCallback(
    (tag: TagLike) => {
      api.tags.untagSingle(
        "ideas",
        tag._id,
        idea._id,
        (updatedTag) => {
          const ownerTags = [...(idea?.ownerTags ?? [])];
          const cIndex = ownerTags.findIndex((t) => t._id === updatedTag._id);
          ownerTags.splice(cIndex, 1);
          const updatedChallenge = updateTagListWithTag(idea, updatedTag);
          util.onUpdateIdea(idea, {
            ownerTags,
            tags: ownerTags.map((t) => t._id),
            ownerChallenge: updatedChallenge,
          });
        },
        () => {},
      );
    },
    [idea, updateTagListWithTag],
  );

  if (!idea.ownerChallenge) {
    return null;
  }

  if (
    !util.canManageChallenge(user!, idea.ownerChallenge) &&
    !util.hasPermission(user!, "challenge.editIdeaTags", idea.challenge)
  ) {
    return null;
  }

  return (
    <TagModalChooser
      trigger={
        <Button tooltip="Assign tags">
          <Icon icon={HashIcon} />
        </Button>
      }
      tags={idea.ownerTags ?? []}
      availableTags={idea.ownerChallenge.tagList?.filter(
        (tag: OpenAPI.Schemas["Tag"]) =>
          !tag?.disabledEntities?.includes("ideas"),
      )}
      addTag={addTag}
      removeTag={removeTag}
      hideView
      canEdit
    />
  );
};

export default TagAction;
